import React, { useState, useCallback, useMemo } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Box,
  CircularProgress,
  Typography,
  Backdrop,
} from '@mui/material'
import {
  Add as AddIcon,
  AutoAwesomeTwoTone as AutoAwesomeIcon,
  Close as CloseIcon,
} from '@mui/icons-material'

import MoveToThemeView from './MoveToThemeView'
import KeyPhrasesView from './KeyPhrasesView'
import AddKeyPhrasesView from './AddKeyPhrasesView'
import type { ThemeKeyPhrasesModalProps } from './Types'
import { useMoveKeyPhrases } from '../Hooks/UseMoveKeyPhrases'
import { useThemeKeyPhrasesModalStyles } from '../Hooks/UseThemeStyles'
import { useAddKeyPhrases } from '../Hooks/UseAddKeyPhrases'
import { useDeleteKeyPhrases } from '../Hooks/UseDeleteKeyPhrases'

enum ViewMode {
  KEY_PHRASES = 'keyPhrases',
  MOVE = 'move',
  ADD = 'add',
}

const ThemeKeyPhrasesModal: React.FC<ThemeKeyPhrasesModalProps> = ({
  open,
  themes,
  themeId,
  onClose,
  handleViewContributionsFromModal,
}) => {
  const styles = useThemeKeyPhrasesModalStyles()
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.KEY_PHRASES)
  const [movingKeyPhrase, setMovingKeyPhrase] = useState<string | null>(null)
  const [confirmationPhrase, setConfirmationPhrase] = useState<string | null>(
    null,
  )
  const { loading: moveLoading, moveKeyPhrase } = useMoveKeyPhrases()
  const { loadingState: deleteLoading, deleteKeyPhrase } = useDeleteKeyPhrases()
  const {
    loading: addLoading,
    addKeyPhraseToTheme,
    allKeyPhrases,
    searchTerm,
    setSearchTerm,
    enterAddMode,
    exitAddMode,
  } = useAddKeyPhrases()

  // Find Current Theme
  const currentTheme = useMemo(() => {
    return themes.find((theme) => theme.id === themeId) || null
  }, [themes, themeId])

  const isLoading =
    moveLoading.isLoading || addLoading.isLoading || deleteLoading.isLoading
  const loadingMessage = moveLoading.isLoading
    ? moveLoading.message
    : addLoading.isLoading
    ? addLoading.message
    : deleteLoading.message

  const themeData = useMemo(() => {
    return {
      id: currentTheme?.id || '',
      name: currentTheme?.attributes?.name || '',
      keyPhrases:
        currentTheme?.attributes?.query?.attributes?.key_phrases || [],
    }
  }, [currentTheme])

  const { keyPhrases } = themeData

  // MOVE: Handle entering move mode
  const handleEnterMoveMode = useCallback((keyPhrase: string) => {
    setViewMode(ViewMode.MOVE)
    setMovingKeyPhrase(keyPhrase)
    setConfirmationPhrase(null)
  }, [])

  // MOVE: Handle exiting Move mode
  const handleExitMoveMode = useCallback(() => {
    setMovingKeyPhrase(null)
    setViewMode(ViewMode.KEY_PHRASES)
  }, [])

  if (!currentTheme) return null

  // MOVE: Handle moving a key phrase
  const handleMoveKeyPhrase = useCallback(
    async (targetTheme) => {
      if (!movingKeyPhrase) return

      const success = await moveKeyPhrase(
        movingKeyPhrase,
        currentTheme,
        targetTheme,
      )
      if (success) {
        handleExitMoveMode()
      }
    },
    [movingKeyPhrase, themeId, moveKeyPhrase, handleExitMoveMode],
  )

  // ADD: Handle entering Add mode
  const handleEnterAddMode = () => {
    setViewMode(ViewMode.ADD)
    setConfirmationPhrase(null)
    enterAddMode()
  }

  // ADD: Handle exiting Add mode
  const handleExitAddMode = useCallback(() => {
    setViewMode(ViewMode.KEY_PHRASES)
    exitAddMode()
  }, [])

  // ADD: Handle adding a key phrase
  const handleAddKeyPhrase = async (keyPhrases: string[]) => {
    const results = await Promise.all(
      keyPhrases.map((keyPhrase) =>
        addKeyPhraseToTheme(currentTheme, keyPhrase),
      ),
    )
    return results.every((result) => result)
  }

  // Handle view contributions
  const handleViewContributions = useCallback(() => {
    handleViewContributionsFromModal(keyPhrases)
  }, [handleViewContributionsFromModal, keyPhrases])

  // Handle search change
  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)
  }

  // Handle deleting a key phrase
  const handleDeleteKeyPhrase = useCallback(
    async (keyPhrase: string) => {
      const success = await deleteKeyPhrase(currentTheme, keyPhrase)
      if (success) {
        setConfirmationPhrase(null) // Clear confirmation after successful deletion
      }
    },
    [currentTheme, deleteKeyPhrase],
  )

  // Handle confirmation phrase change
  const handleConfirmationChange = useCallback((phrase: string | null) => {
    setConfirmationPhrase(phrase)
  }, [])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid="theme-key-phrases-modal"
      maxWidth="sm"
      fullWidth
      disablePortal
    >
      <DialogTitle sx={styles.dialogTitle}>
        <Box sx={styles.titleBox}>
          <AutoAwesomeIcon fontSize="small" sx={styles.iconColor} />
          {themeData.name}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isLoading}
          data-testid="close-modal-button"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={styles.dialogContent}>
        {viewMode === 'move' && movingKeyPhrase ? (
          <MoveToThemeView
            selectedKeyPhrase={movingKeyPhrase}
            themeId={themeId}
            themes={themes}
            onMove={handleMoveKeyPhrase}
            onExitMoveMode={handleExitMoveMode}
          />
        ) : viewMode === 'add' ? (
          <AddKeyPhrasesView
            currentTheme={currentTheme}
            onExitAddMode={handleExitAddMode}
            onAddKeyPhrase={handleAddKeyPhrase}
            allKeyPhrases={allKeyPhrases}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            loading={addLoading.isLoading}
          />
        ) : (
          <KeyPhrasesView
            keyPhrases={keyPhrases}
            onMoveClick={handleEnterMoveMode}
            onDeleteClick={handleDeleteKeyPhrase}
            confirmationPhrase={confirmationPhrase}
            onConfirmationChange={handleConfirmationChange}
          />
        )}

        {/* Loading Backdrop */}
        {isLoading && (
          <Backdrop
            open={isLoading}
            sx={styles.backdrop}
            data-testid="loading-backdrop"
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {loadingMessage}
            </Typography>
          </Backdrop>
        )}
      </DialogContent>

      <DialogActions>
        {viewMode === 'keyPhrases' && (
          <Button
            startIcon={<AddIcon />}
            onClick={handleEnterAddMode}
            sx={{ mr: 'auto' }}
            disabled={isLoading || !!confirmationPhrase}
          >
            Add key phrases
          </Button>
        )}
        <Button
          onClick={handleViewContributions}
          disabled={isLoading || !!confirmationPhrase}
        >
          View Contributions
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default React.memo(ThemeKeyPhrasesModal)
